import { TGroupedGetAllAvailablePrizesResponse } from '@daymade/api-contracts';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import MainLayout from 'components/layout/MainLayout';
import PageTitle from 'components/PageTitle';
import { MainDrawScheduleCode, MainDrawTypeCode } from 'constants/Draw';
import { MAIN_PRIZE_CATEGORIES_2026 } from 'constants/PrizeCategories';
import { NextSeo } from 'next-seo';
import { useEffect, useMemo, useState } from 'react';
import {
  EecProduct,
  getProductListTrackingName,
  trackEecImpression,
} from 'utils/analytics/analytics';
import { tryApi } from 'utils/api/common';
import { prizeApiClient } from 'utils/apiClient';
import { ASSET_PREFIX, PRIZE_NUMBERS } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import styles from './AllPrizesByCategory.module.css';
import { prizePageIndexMetadata } from './AllPrizesPageShared';
import PrizeCategoryCarousel from './PrizeCategoryCarousel';
import { SearchFilter } from './SearchFilter';
import SubmitPrizeSection2026 from 'components/SubmitPrizeSection2026';
import RoundedButton from 'components/RoundedButton';

const AllPrizesByCategory = () => {
  const [loading, setLoading] = useState(false);
  const [groupedPrizes, setGroupedPrizes] =
    useState<TGroupedGetAllAvailablePrizesResponse>();

  useEffect(() => {
    trackEecImpression(getProductListTrackingName(''), EecProduct.LOTTERY);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const result = await tryApi(
        prizeApiClient.public.getGroupedPrizes({
          query: {
            drawTypeCode: MainDrawTypeCode,
            drawScheduleCode: MainDrawScheduleCode.TUESDAY,
            count: 8,
            groupBy: 'mainCategory',
          },
        }),
      );
      if (result.status === HttpStatusCode.OK) {
        setGroupedPrizes(result.body);
      } else {
        throw result.error;
      }
      setLoading(false);
    })();
  }, []);

  const groupedPrizesWithCategoryInfo = useMemo(() => {
    if (!groupedPrizes) {
      return undefined;
    }
    const orderOfCategories = Object.values(MAIN_PRIZE_CATEGORIES_2026);

    const result = orderOfCategories.map((categoryInfo) => ({
      category: categoryInfo,
      prizes: groupedPrizes[categoryInfo.categoryCode] || [],
    }));
    return result;
  }, [groupedPrizes]);

  return (
    <MainLayout additionalClassName="prizesPage" limitItemWidths>
      <NextSeo
        title={prizePageIndexMetadata.metaTitle}
        description={prizePageIndexMetadata.metaDescription}
        openGraph={{
          title: prizePageIndexMetadata.metaTitle,
          description: prizePageIndexMetadata.metaDescription,
        }}
      />

      <div className="aboutUsTypographyReset">
        <PageTitle
          rebrandDm2025
          heroBackgroundImage={`${ASSET_PREFIX}/images/prizes/hero-background.svg`}
        >
          Prizes
        </PageTitle>
        <AboutUsModule background="grey">
          <AboutUsSiteContainer>
            <SearchFilter filterPlaceholder="Tiers" filterType="tier" />
            <p className={styles.introParagraph}>
              Win {PRIZE_NUMBERS.PREMIUM} epic prizes from beauty boxes to Bali
              brunch. Explore them all below!
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="white">
          <div className={styles.groupingContainer}>
            {groupedPrizesWithCategoryInfo &&
              groupedPrizesWithCategoryInfo.map((x, i) => {
                return (
                  <PrizeCategoryCarousel
                    key={i}
                    categoryTitle={x.category.name}
                    categorySubtitle={x.category.description}
                    image={x.category.svg}
                    prizes={x.prizes}
                    showPrizeTierBadge={true}
                    groupingPageHref={'/prizes/' + x.category.categoryCode}
                    groupingImageSrc={x.category.examplePrizeImage}
                  />
                );
              })}
          </div>
          <div className={styles.backToTopContainer}>
            <RoundedButton href="#" rebrandDm2025 colorStyle={'OutlineDark'}>
              Return to top
            </RoundedButton>
          </div>
        </AboutUsModule>
        <SubmitPrizeSection2026 />
      </div>
    </MainLayout>
  );
};

export default AllPrizesByCategory;
